import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Megaphone, MessageSquareText, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { readApiJson } from '../lib/api';
import { reportAnnouncementEvent } from '../utils/announcementEvents';
import OfferBanner from './OfferBanner';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const FOCUS_REFRESH_THROTTLE_MS = 30 * 1000;

const KIND_DETAILS = {
  update: {
    label: 'Update',
    Icon: Megaphone,
    className: 'border-[#3d5575] bg-[#0d1a31]/95',
    accentClassName: 'text-[#82b2ff]'
  },
  feedback: {
    label: 'Feedback',
    Icon: MessageSquareText,
    className: 'border-[#286568] bg-[#09262d]/95',
    accentClassName: 'text-[#79e1dd]'
  },
  notice: {
    label: 'Notice',
    Icon: Bell,
    className: 'border-[#6f5b32] bg-[#241c0d]/95',
    accentClassName: 'text-[#f4c86d]'
  }
};

const AnnouncementBanner = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [dismissingId, setDismissingId] = useState(null);
  const lastLoadedAtRef = useRef(0);
  const activeUserIdRef = useRef(user?.id || null);
  activeUserIdRef.current = user?.id || null;

  const loadAnnouncements = useCallback(async ({ force = false } = {}) => {
    if (!user?.id) return;
    const now = Date.now();
    if (!force && now - lastLoadedAtRef.current < FOCUS_REFRESH_THROTTLE_MS) return;
    lastLoadedAtRef.current = now;

    try {
      const data = await readApiJson('/announcements');
      if (activeUserIdRef.current === user.id) {
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      // Announcements are supplementary and should never interrupt the application.
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setAnnouncements([]);
      lastLoadedAtRef.current = 0;
      return undefined;
    }

    void loadAnnouncements({ force: true });
    const onFocus = () => void loadAnnouncements();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void loadAnnouncements();
    };
    const onAnnouncementsUpdated = () => void loadAnnouncements({ force: true });
    const interval = window.setInterval(
      () => void loadAnnouncements({ force: true }),
      POLL_INTERVAL_MS
    );

    window.addEventListener('focus', onFocus);
    window.addEventListener('cbat:announcements-updated', onAnnouncementsUpdated);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('cbat:announcements-updated', onAnnouncementsUpdated);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadAnnouncements, user?.id]);

  const dismiss = async (announcement) => {
    if (dismissingId) return;
    setDismissingId(announcement.id);
    setAnnouncements((current) => current.filter((item) => item.id !== announcement.id));
    try {
      await readApiJson(`/announcements/${announcement.id}/dismiss`, { method: 'POST' });
    } catch (error) {
      void loadAnnouncements({ force: true });
    } finally {
      setDismissingId(null);
    }
  };

  const announcement = announcements[0];
  const shownId = announcement && !location.pathname.startsWith('/module/') ? announcement.id : null;
  const userId = user?.id;

  // The administrator's counts: this person has seen the one on screen.
  useEffect(() => {
    if (shownId) reportAnnouncementEvent(userId, shownId, 'seen');
  }, [shownId, userId]);

  if (!announcement || location.pathname.startsWith('/module/')) return null;

  const reportClick = () => reportAnnouncementEvent(userId, announcement.id, 'click');

  if (announcement.kind === 'offer') {
    return (
      <OfferBanner
        announcement={announcement}
        onDismiss={() => void dismiss(announcement)}
        onAction={reportClick}
        dismissing={Boolean(dismissingId)}
        count={announcements.length}
      />
    );
  }

  const details = KIND_DETAILS[announcement.kind] || KIND_DETAILS.notice;
  const { Icon } = details;

  return (
    <section
      className={`announcement-banner border-b ${details.className}`}
      aria-label="Account announcement"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-[1320px] flex-col gap-3 px-4 py-3 text-white sm:flex-row sm:items-center sm:px-6">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-current bg-black/20 ${details.accentClassName}`}>
            <Icon size={17} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className={`font-mono text-[9px] font-semibold uppercase tracking-[0.14em] ${details.accentClassName}`}>
                {details.label}
              </span>
              <h2 className="break-words text-sm font-bold">{announcement.title}</h2>
            </div>
            <p className="mt-1 whitespace-pre-wrap break-words text-xs leading-relaxed text-[#b9cbdd]">
              {announcement.message}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
          {announcement.action_label && announcement.action_path && (
            <Link
              to={announcement.action_path}
              onClick={reportClick}
              className="site-button site-button--primary !min-h-8 shrink-0 !py-1.5"
            >
              {announcement.action_label}
            </Link>
          )}
          {announcements.length > 1 && (
            <span className="shrink-0 font-mono text-[10px] text-[#C0CCEE]">
              1 / {announcements.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => void dismiss(announcement)}
            disabled={Boolean(dismissingId)}
            title="Dismiss announcement"
            aria-label="Dismiss announcement"
            className="site-icon-button h-8 w-8 shrink-0 disabled:opacity-50"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default AnnouncementBanner;
